import React, { ReactNode, useEffect, useState } from 'react';
import { Navigation } from './Navigation';
import { Footer } from './Footer';
import { AdSpace } from './AdSpace';
import { MonitorSmartphone, X } from 'lucide-react';
import { trackEvent } from '../utils/analytics';
import { scheduleDeferredAdsenseLoad } from '../utils/loadAdsense';

interface LayoutProps {
  children: ReactNode;
}

const MOBILE_BANNER_KEY = 'mobile-warning-dismissed';

const MobileBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(MOBILE_BANNER_KEY);
    if (!dismissed && window.innerWidth < 768) {
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    localStorage.setItem(MOBILE_BANNER_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center gap-3 text-amber-800 text-sm">
      <MonitorSmartphone size={18} className="shrink-0 text-amber-600" />
      <p className="flex-1">
        <span className="font-semibold">Best viewed on a larger screen.</span>{' '}
        For the full calculator experience, we recommend using a laptop or desktop computer.
      </p>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="shrink-0 p-1 rounded hover:bg-amber-100 transition-colors"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  useEffect(() => {
    trackEvent('mortgage_calculator_view', {
      page_type: 'calculator',
    });
  }, []);

  useEffect(() => {
    scheduleDeferredAdsenseLoad();
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-cream">
      <Navigation />
      <MobileBanner />
      
      <main className="flex-grow flex flex-col md:flex-row">
        {/* Left Ad Space - Hidden on mobile */}
        <div className="hidden md:block md:w-1/6 xl:w-1/5 bg-cream">
          <AdSpace position="left" />
        </div>
        
        {/* Main Content Area */}
        <div className="w-full md:w-4/6 xl:w-3/5">
          {children}
        </div>
        
        {/* Right Ad Space - Hidden on mobile */}
        <div className="hidden md:block md:w-1/6 xl:w-1/5 bg-cream">
          <AdSpace position="right" />
        </div>
      </main>
      
      <Footer />
    </div>
  );
};